import { AuthService } from '@/app/core/services/auth.service';
import { PermissionService } from '@/app/core/services/permission.service';
import { UserProfile, UsersService } from '@/app/core/services/users.service';
import {
  UserNotificationsService,
  UserNotification,
} from '@/app/core/services/user-notifications.service';
import { CommonModule, formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);
  private usersService = inject(UsersService);
  private elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private notificationsService = inject(UserNotificationsService);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('notificationMenuRef') notificationMenuRef?: ElementRef<HTMLElement>;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as Node | null;
    const notificationMenu = this.notificationMenuRef?.nativeElement;

    if (notificationMenu && target && !notificationMenu.contains(target)) {
      this.notificationsOpen = false;
    }

    if (!this.elementRef.nativeElement.contains(target)) {
      this.menuOpen = false;
    }
  }

  menuOpen = false;
  notificationsOpen = false;
  notificationsLoading = false;
  user: UserProfile | null = null;
  notifications: UserNotification[] = [];
  unreadCount = 0;

  currentPage = 1;
  hasMore = true;
  isLoadingMore = false;
  autoRefreshTimer: ReturnType<typeof setTimeout> | undefined;

  @Input() isCollapsed = false;
  @Input() isMobileOpen = false;

  @Output() sidebarToggle = new EventEmitter<void>();

  ngOnInit() {
    this.loadProfile();
    // Only fetch the unread count initially.
    // The full notification list API will be called when the user clicks the icon.
    this.refreshUnreadCount();
    this.setupAutoRefresh();
  }

  ngOnDestroy(): void {
    if (this.autoRefreshTimer) {
      clearTimeout(this.autoRefreshTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProfile() {
    this.usersService.getProfile().subscribe({
      next: (res) => {
        this.user = res;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Profile load failed', err);
      },
    });
  }

  get avatarLetter(): string {
    return this.user?.first_name ? this.user.first_name.charAt(0).toUpperCase() : '';
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
    if (this.menuOpen) {
      this.notificationsOpen = false;
    }
    this.cdr.detectChanges();
  }

  toggleNotifications(event: Event) {
    event.stopPropagation();
    this.notificationsOpen = !this.notificationsOpen;
    if (this.notificationsOpen) {
      this.menuOpen = false;
    }
    if (this.notificationsOpen) {
      // If we are opening, fetch the first page to ensure we have the latest
      this.loadNotifications({ silent: this.notifications.length > 0, reset: true });
    }
    this.cdr.detectChanges();
  }

  loadNotifications(options?: { silent?: boolean; reset?: boolean }) {
    const silent = options?.silent === true;
    const reset = options?.reset === true;

    if (reset) {
      this.currentPage = 1;
      this.hasMore = true;
      // Do not clear this.notifications here to prevent UI blink
    }

    if (!silent && !this.notifications.length) {
      this.notificationsLoading = true;
    }
    this.notificationsService
      .getNotifications({ page: this.currentPage, limit: 20 })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.notificationsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          const items = res.data ?? [];
          if (reset || this.currentPage === 1) {
            this.notifications = items;
          } else {
            const existingIds = new Set(this.notifications.map((n) => n.id));
            const filteredItems = items.filter((n) => !existingIds.has(n.id));
            this.notifications = [...this.notifications, ...filteredItems];
          }

          this.unreadCount =
            res.meta.unread_total ?? this.notifications.filter((n) => !n.isRead).length;

          if (res.meta.page && res.meta.total_pages && res.meta.page >= res.meta.total_pages) {
            this.hasMore = false;
          } else if (items.length < 20) {
            this.hasMore = false;
          } else {
            this.hasMore = true;
          }
        },
        error: (err) => {
          console.error('Notification load failed', err);
        },
      });
  }

  setupAutoRefresh() {
    this.autoRefreshTimer = setTimeout(() => {
      this.refreshUnreadCount();
      this.setupAutoRefresh();
    }, 10000);
  }

  refreshUnreadCount() {
    this.notificationsService
      .getUnreadCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          const previousCount = this.unreadCount;
          this.unreadCount = count;

          // Handle real-time update if panel is open and there are new notifications
          if (this.notificationsOpen && count > previousCount) {
            const diff = count - previousCount;
            this.fetchNewNotifications(diff);
          }
          this.cdr.detectChanges();
        },
        error: () => {
          // silent error for background polling
        },
      });
  }

  fetchNewNotifications(limit = 20) {
    // Fetch only the latest new notifications
    this.notificationsService
      .getNotifications({ page: 1, limit })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const newItems = res.data ?? [];
          // Prepend items that we don't already have
          const existingIds = new Set(this.notifications.map((n) => n.id));
          const itemsToAdd = newItems.filter((n) => !existingIds.has(n.id));

          if (itemsToAdd.length > 0) {
            this.notifications = [...itemsToAdd, ...this.notifications];
            this.cdr.detectChanges();
          }

          this.unreadCount = res.meta.unread_total ?? this.unreadCount;
        },
      });
  }

  onScroll(event: Event) {
    const target = event.target as HTMLElement;
    if (!target) return;

    const threshold = 10;
    const position = target.scrollTop + target.clientHeight;
    // Check if scrolled to bottom
    if (position >= target.scrollHeight - threshold) {
      this.loadMoreNotifications();
    }
  }

  loadMoreNotifications() {
    if (this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;
    this.currentPage++;

    this.notificationsService
      .getNotifications({ page: this.currentPage, limit: 20 })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingMore = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          const newItems = res.data ?? [];
          if (newItems.length === 0) {
            this.hasMore = false;
          } else {
            // Avoid duplicates just in case
            const existingIds = new Set(this.notifications.map((n) => n.id));
            const filteredNew = newItems.filter((n) => !existingIds.has(n.id));
            this.notifications = [...this.notifications, ...filteredNew];

            // Check if we've reached the end
            if (res.meta.page && res.meta.total_pages && res.meta.page >= res.meta.total_pages) {
              this.hasMore = false;
            } else if (newItems.length < 20) {
              this.hasMore = false;
            }
          }
        },
        error: (err) => {
          console.error('Failed to load more notifications', err);
          this.currentPage--; // Revert page on error
        },
      });
  }

  markAsRead(notification: UserNotification, event: Event) {
    event.stopPropagation();
    if (notification.isRead) return;
    this.notificationsService.markAsRead(notification.id).subscribe({
      next: () => {
        notification.isRead = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Mark as read failed', err);
      },
    });
  }

  markAllAsRead(event: Event) {
    event.stopPropagation();
    if (!this.notifications.length) return;
    this.notificationsService.markAllAsRead().subscribe({
      next: () => {
        this.notifications = this.notifications.map((n) => ({ ...n, isRead: true }));
        this.unreadCount = 0;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Mark all as read failed', err);
      },
    });
  }

  formatNotificationTime(value?: string) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return formatDate(date, 'dd MMM y, h:mm a', 'en-US');
  }

  get isMobile(): boolean {
    return window.innerWidth < 768;
  }

  toggleSidebar() {
    this.sidebarToggle.emit();
  }

  logout(event: Event) {
    event.stopPropagation();
    this.authService.logout();
    this.permissionService.clear();
  }
}
